import React, { useMemo } from 'react';
import { Animated, ScrollView, Text, View } from 'react-native';
import { CheckCircle, Flag, Medal, Star, Swords, Trophy, Zap, type LucideIcon } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { SketchFrame } from '../../worldcup/WorldCupWelcomeScreen/SketchFrame';
import { WelcomeDisclaimerFooter } from '../../worldcup/WorldCupWelcomeScreen/WelcomeDisclaimerFooter';
import { getWelcomePointsPhaseStyles } from '../../worldcup/WorldCupWelcomeScreen/WelcomePointsPhase.styles';
import {
  useWorldCupWelcomeStaggerEntrance,
  staggerStyle,
} from '../../worldcup/WorldCupWelcomeScreen/useWorldCupWelcomeStaggerEntrance';
import {
  buildPointsTimeline,
  type CompetitionPointsTimelineIconId,
} from '../../../src/competitions/helpers/competitionPointsTimeline.helper';
import type { CompetitionIntroConfig } from '../../../src/competitions/types/competitionIntro.types';
import { COMPETITION_WELCOME_POINTS_BLOCK_COUNT } from './CompetitionWelcomeScreen.constants';
import { getCompetitionWelcomePhaseStyles } from './CompetitionWelcomeScreen.styles';
import { CompetitionWelcomeFooter } from './CompetitionWelcomeFooter';

const TIMELINE_STAGE_ICONS: Record<CompetitionPointsTimelineIconId, LucideIcon> = {
  flag: Flag,
  swords: Swords,
  trophy: Trophy,
  medal: Medal,
};

export interface CompetitionWelcomePointsPhaseProps {
  content: CompetitionIntroConfig;
  phaseIndex: number;
  phaseCount: number;
  isExiting: boolean;
  scrollContentPaddingTop: number;
  bottomInset: number;
  onNext: () => void;
  testID?: string;
  /** Stable across phases so a walkthrough can click "next" without knowing the beat. */
  ctaTestID?: string;
}

/**
 * Points beat: the stage ladder drawn as the World Cup draws it — a zig-zag timeline — then
 * the sketch-framed explainer notes (specs/champions-league, Story 13).
 *
 * The timeline is derived from `content.points.rows`, so it works at any stage count. The WC's
 * own `WELCOME_POINTS_TIMELINE_LAYOUT` is deliberately not imported: it is seven hardcoded
 * entries and its module throws at load unless that count matches the WC's own table.
 */
export const CompetitionWelcomePointsPhase: React.FC<CompetitionWelcomePointsPhaseProps> = ({
  content,
  phaseIndex,
  phaseCount,
  isExiting,
  scrollContentPaddingTop,
  bottomInset,
  onNext,
  testID,
  ctaTestID,
}) => {
  const theme = useTheme();
  const styles = useMemo(
    () => getCompetitionWelcomePhaseStyles(theme, { scrollContentPaddingTop, bottomInset }),
    [theme, scrollContentPaddingTop, bottomInset],
  );
  // The timeline's own geometry comes from the World Cup screen, unchanged, so the two intros
  // cannot drift apart the way the flat table did.
  const timelineStyles = useMemo(
    () => getWelcomePointsPhaseStyles(theme, { scrollContentPaddingTop: 0 }),
    [theme],
  );
  const stagger = useWorldCupWelcomeStaggerEntrance(
    phaseIndex,
    phaseIndex,
    COMPETITION_WELCOME_POINTS_BLOCK_COUNT,
  );

  const { entries, showTrack } = useMemo(
    () => buildPointsTimeline(content.points.rows, content.points.goldenBallStages),
    [content.points.rows, content.points.goldenBallStages],
  );
  const goldenBallLabel = content.points.goldenBallLabel ?? '';

  return (
    <View style={styles.container} testID={testID}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        testID={testID != null ? `${testID}-scroll` : undefined}
      >
        <Animated.View style={staggerStyle(stagger[0])}>
          <Text style={styles.eyebrow}>{content.points.label}</Text>
          <Text style={styles.title}>{content.points.title}</Text>
        </Animated.View>

        <Animated.View style={staggerStyle(stagger[1])}>
          <View style={timelineStyles.timelineContainer}>
            {showTrack && <View style={timelineStyles.timelineLine} />}
            {entries.map((entry) => {
              const StageIcon = TIMELINE_STAGE_ICONS[entry.icon];
              const card = (
                <View style={timelineStyles.stageCard}>
                  <View style={timelineStyles.stageCardIconWrap}>
                    <StageIcon size={20} color={theme.primaryDark} strokeWidth={2.2} />
                  </View>
                  <View style={timelineStyles.stageCardBody}>
                    <Text style={timelineStyles.stageCardTitle}>{entry.stage}</Text>
                    {entry.compact ? (
                      <View style={timelineStyles.pointsRowCompact}>
                        <View style={timelineStyles.pointsStatGroup}>
                          <Star size={12} color={theme.success} strokeWidth={2.4} />
                          <Text style={timelineStyles.pointsText}>{entry.hit}</Text>
                        </View>
                        <View style={timelineStyles.pointsStatGroup}>
                          <CheckCircle size={12} color={theme.info} strokeWidth={2.4} />
                          <Text style={timelineStyles.pointsText}>{entry.result}</Text>
                        </View>
                      </View>
                    ) : (
                      <>
                        <View style={timelineStyles.pointsLine}>
                          <Star size={12} color={theme.success} strokeWidth={2.4} />
                          <Text style={timelineStyles.pointsText}>
                            {entry.hit} {content.points.colExact}
                          </Text>
                        </View>
                        <View style={timelineStyles.pointsLine}>
                          <CheckCircle size={12} color={theme.info} strokeWidth={2.4} />
                          <Text style={timelineStyles.pointsText}>
                            {entry.result} {content.points.colOutcome}
                          </Text>
                        </View>
                        {entry.goldenBall && goldenBallLabel !== '' && (
                          <View style={[timelineStyles.pointsLine, timelineStyles.pointsLineLast]}>
                            <Zap size={12} color={theme.accentDark} strokeWidth={2.4} />
                            <Text style={timelineStyles.pointsTextGolden}>{goldenBallLabel}</Text>
                          </View>
                        )}
                      </>
                    )}
                  </View>
                </View>
              );

              return (
                <View key={entry.id} style={timelineStyles.stageRow}>
                  <View style={[timelineStyles.halfColumn, timelineStyles.halfColumnLeft]}>
                    {entry.align === 'left' ? card : null}
                  </View>
                  <View style={timelineStyles.centerTrack}>
                    <View style={timelineStyles.timelineDot} />
                  </View>
                  <View style={[timelineStyles.halfColumn, timelineStyles.halfColumnRight]}>
                    {entry.align === 'right' ? card : null}
                  </View>
                </View>
              );
            })}
          </View>
        </Animated.View>

        {content.points.notes.map((note, i) => {
          const block =
            stagger[Math.min(i + 2, COMPETITION_WELCOME_POINTS_BLOCK_COUNT - 1)];
          return (
            <Animated.View key={note.title} style={[styles.cardSpacer, staggerStyle(block)]}>
              <SketchFrame radius={20} seed={i + 3}>
                <View style={styles.card}>
                  <View style={styles.cardBody}>
                    <Text style={styles.ruleTitle}>{note.title}</Text>
                    <Text style={styles.ruleDesc}>{note.body}</Text>
                  </View>
                </View>
              </SketchFrame>
            </Animated.View>
          );
        })}

        <WelcomeDisclaimerFooter />
      </ScrollView>

      <View style={styles.footer} testID={testID != null ? `${testID}-footer` : undefined}>
        <CompetitionWelcomeFooter
          label={content.ctaEnterApp}
          isExiting={isExiting}
          phaseIndex={phaseIndex}
          phaseCount={phaseCount}
          styles={styles}
          onPress={onNext}
          testID={ctaTestID}
        />
      </View>
    </View>
  );
};
